import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, defer, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, ignoreElements, map, shareReplay, switchMap, take } from 'rxjs/operators';
import moment from 'moment';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import { IAuthenticationService, IUserInfo } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IErrorStatus } from '@nimbus-library/features/shared/models/interfaces';
import { CookieService } from '@nimbus-library/features/shared/services/cookies/cookies.service';
import { LocalizationPreferencesModel } from '@nimbus-library/features/user-settings/models/localization-preferences.model';
import { LocalizationPreferencesApiClient } from '@nimbus-library/features/user-settings/services/localization-preferences.api-client';
import { DateFormat, debugLanguageEnvironments, Language } from '../models/enums/language.enum';
import { ILocalizationService } from './i-localization.service';

type PreferenceKey = keyof LocalizationPreferencesModel;

// The server holds the user's choice; the cookies are only a cache for the first paint. A cookie fills a value
// the server doesn't have yet once (migration), so existing users keep their choice.
@Injectable()
export abstract class PersistedLocalizationService extends ILocalizationService {
  // The same lists LocalizationState ends up offering, so an excluded value is never persisted from here.
  private readonly effectiveLanguages: Language[];
  private readonly effectiveDateFormats: DateFormat[];
  private readonly isDebugEnvironment: boolean;
  private current: LocalizationPreferencesModel = { language: null, dateFormat: null };
  private reconciledOwner: string | null = null;
  // Picked but not yet written; also covers a pick made before sign-in.
  private pendingSelection: Partial<LocalizationPreferencesModel> = {};
  // Bumped on every explicit save; a load that started before it must not emit its (stale) result.
  private readonly selectionVersion: Record<PreferenceKey, number> = { language: 0, dateFormat: 0 };
  // Server calls run one after another; a PUT replaces both fields, so each must see the previous one's result.
  private queue$: Observable<never> = EMPTY;

  protected abstract readonly languageCookieKey: string;
  protected abstract readonly dateFormatCookieKey: string;
  // Owner-scoped cookies carry `tenantId/userId`, so another user on a shared browser never adopts (or persists) them.
  protected abstract readonly ownerScopedCookies: boolean;

  public constructor(
    protected readonly cookieService: CookieService,
    protected readonly preferencesApiClient: LocalizationPreferencesApiClient,
    protected readonly authenticationService: IAuthenticationService,
    protected readonly environmentConfigurationService: EnvironmentConfigurationService,
  ) {
    super();
    this.isDebugEnvironment = debugLanguageEnvironments.includes(
      this.environmentConfigurationService?.configurationSettings?.environmentName,
    );
    const excluded: string[] = this.environmentConfigurationService?.configurationSettings?.lang_exclude_list ?? [];
    const languages = this.getSupportedLanguages() ?? Object.values(Language);
    this.effectiveLanguages = languages.filter((language) => language !== Language.Debug && !excluded.includes(language));
    this.effectiveDateFormats = Object.values(DateFormat).filter((format) => format !== DateFormat.Debug && !excluded.includes(format));
  }

  public loadUserLanguage(): Observable<Language | null> {
    const browserDefault = (): Language => this.getBrowserLanguage(this.effectiveLanguages, Language.English);
    if (!this.isDebugEnvironment || this.readCookie(this.languageCookieKey).value !== Language.Debug) {
      return this.load('language', browserDefault);
    }

    // Debug is a local-only choice the server doesn't accept; it's kept only for the user who picked it.
    const user = this.getSignedInUser();
    const debugOrStored$ = this.signedInUser$(user).pipe(
      switchMap((signedInUser) =>
        this.readOwnCookie(this.languageCookieKey, signedInUser) === Language.Debug
          ? of(Language.Debug)
          : this.load('language', browserDefault),
      ),
    );
    return user ? debugOrStored$ : concat(of(browserDefault()), debugOrStored$);
  }

  public saveUserLanguage(language: Language): Observable<void> {
    return this.save({ language });
  }

  public loadUserDateFormat(): Observable<DateFormat | null> {
    return this.load('dateFormat', () => this.getBrowserDateformat(this.effectiveDateFormats, DateFormat.English));
  }

  public saveUserDateFormat(dateFormat: DateFormat): Observable<void> {
    return this.save({ dateFormat });
  }

  protected readCachedDateFormat(user: IUserInfo | null): DateFormat | null {
    return this.toDateFormat(this.readOwnCookie(this.dateFormatCookieKey, user));
  }

  // Before sign-in the owner is unknown, so any cookie serves the first paint. A value without an owner predates
  // owner scoping and is taken as the user's own.
  protected readOwnCookie(cookieKey: string, user: IUserInfo | null): string | null {
    const { value, owner } = this.readCookie(cookieKey);
    return !user || owner === undefined || owner === this.cookieOwner(user) ? value : null;
  }

  protected toDateFormat(value: string | null | undefined): DateFormat | null {
    return this.effectiveDateFormats.find((dateFormat) => dateFormat === value) ?? null;
  }

  // Emits the cached value right away, then the stored one when it differs. `null` means nothing is stored
  // anywhere, so the state falls back to the browser locale and saves that.
  private load<K extends PreferenceKey>(
    key: K,
    browserDefault: () => LocalizationPreferencesModel[K],
  ): Observable<LocalizationPreferencesModel[K]> {
    const versionAtLoad = this.selectionVersion[key];
    const user = this.getSignedInUser();
    const cached = this.readCached(key, user);
    // Pages shown before sign-in (login, consent) still need a language; emitting the browser locale ourselves
    // keeps the state from saving it as a choice.
    const firstPaint = cached ?? (user ? null : browserDefault());

    const stored$ = this.signedInUser$(user).pipe(
      switchMap((signedInUser) => this.enqueue(() => this.reconcile(signedInUser))),
      filter(() => this.selectionVersion[key] === versionAtLoad),
      map((reconciled) => (reconciled ? this.current[key] : (cached ?? browserDefault()))),
    );
    return firstPaint ? concat(of(firstPaint), stored$.pipe(filter((value) => value !== firstPaint))) : stored$;
  }

  private save(selection: Partial<LocalizationPreferencesModel>): Observable<void> {
    Object.keys(selection).forEach((key: PreferenceKey) => this.selectionVersion[key]++);
    this.pendingSelection = { ...this.pendingSelection, ...selection };
    const user = this.getSignedInUser();
    this.writeCookies(selection, user ? this.cookieOwner(user) : '');
    if (!user) {
      return of(undefined);
    }

    return this.enqueue(() =>
      this.reconcile(user).pipe(
        switchMap((reconciled) => {
          // Without the stored values a PUT could clear the other field; the cookie keeps the pick meanwhile.
          if (!reconciled) {
            return of(undefined);
          }
          const next = { ...this.current, ...this.pendingSelection };
          this.pendingSelection = {};
          if (next.language === this.current.language && next.dateFormat === this.current.dateFormat) {
            return of(undefined);
          }
          this.current = next;
          this.writeCookies(next, this.cookieOwner(user));
          return this.put(user, next);
        }),
      ),
    );
  }

  // Reads the stored values once per user. A pending pick wins, then the stored value; a cookie only fills a gap.
  // Whatever differs from the server is written back. Only 404 or `null` means "not stored": any other failure never writes to the server.
  private reconcile(user: IUserInfo): Observable<boolean> {
    const owner = this.cookieOwner(user);
    if (this.reconciledOwner === owner) {
      return of(true);
    }

    return defer(() => this.preferencesApiClient.get(user.userId, user.tenantId)).pipe(
      catchError((error: IErrorStatus) => (error?.httpStatus === HttpStatusCode.NotFound ? of(null) : throwError(() => error))),
      switchMap((preferences) => {
        const stored: LocalizationPreferencesModel = {
          language: this.toLanguage(preferences?.language),
          dateFormat: this.toDateFormat(preferences?.dateFormat),
        };
        this.current = {
          language: this.pendingSelection.language ?? stored.language ?? this.readCached('language', user),
          dateFormat: this.pendingSelection.dateFormat ?? stored.dateFormat ?? this.readCached('dateFormat', user),
        };
        this.pendingSelection = {};
        this.reconciledOwner = owner;
        this.writeCookies(this.current, owner);
        const changed = this.current.language !== stored.language || this.current.dateFormat !== stored.dateFormat;
        return changed ? this.put(user, this.current) : of(undefined);
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  // The language is required by the API, so nothing is written until one is known.
  private put(user: IUserInfo, preferences: LocalizationPreferencesModel): Observable<void> {
    const language = this.toLanguage(preferences.language);
    if (!language) {
      return of(undefined);
    }
    return this.preferencesApiClient
      .save(user.userId, user.tenantId, { language, dateFormat: this.toDateFormat(preferences.dateFormat) })
      .pipe(
        map((): void => undefined),
        catchError(() => of(undefined)),
      );
  }

  private enqueue<T>(operation: () => Observable<T>): Observable<T> {
    const run$ = concat(this.queue$, defer(operation)).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.queue$ = run$.pipe(
      ignoreElements(),
      catchError(() => EMPTY),
    );
    return run$;
  }

  private signedInUser$(user: IUserInfo | null): Observable<IUserInfo> {
    return user
      ? of(user)
      : this.authenticationService.isAuthenticated$.pipe(
          map(() => this.getSignedInUser()),
          filter((signedInUser): signedInUser is IUserInfo => !!signedInUser),
          take(1),
        );
  }

  private getSignedInUser(): IUserInfo | null {
    const user = this.authenticationService.getUserInfo();
    return user?.userId && user?.tenantId ? user : null;
  }

  private readCached<K extends PreferenceKey>(key: K, user: IUserInfo | null): LocalizationPreferencesModel[K] {
    return (
      key === 'language' ? this.toLanguage(this.readOwnCookie(this.languageCookieKey, user)) : this.readCachedDateFormat(user)
    ) as LocalizationPreferencesModel[K];
  }

  private readCookie(cookieKey: string): { value: string | null; owner: string | undefined } {
    const [value, owner] = (this.cookieService.getCookie(cookieKey) ?? '').split('|');
    return { value: value || null, owner };
  }

  // An empty owner marks a value chosen before sign-in.
  private writeCookies(preferences: Partial<LocalizationPreferencesModel>, owner: string): void {
    const suffix = this.ownerScopedCookies ? `|${owner}` : '';
    const expires = moment().add(1, 'year');
    if (preferences.language) {
      this.cookieService.setCookie(this.languageCookieKey, `${preferences.language}${suffix}`, expires);
    }
    if (preferences.dateFormat) {
      this.cookieService.setCookie(this.dateFormatCookieKey, `${preferences.dateFormat}${suffix}`, expires);
    }
  }

  private cookieOwner(user: IUserInfo): string {
    return `${user.tenantId}/${user.userId}`;
  }

  private toLanguage(value: string | null | undefined): Language | null {
    return this.effectiveLanguages.find((language) => language === value) ?? null;
  }
}
